<template>
  <teleport to="body">
    <transition name="survey-invite-fade">
      <div v-if="visible" class="survey-invite-backdrop" @click.self="dismiss">
        <section
          class="survey-invite-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="survey-invite-title"
          aria-describedby="survey-invite-description"
        >
          <button
            class="survey-invite-close modal-icon-dismiss-action"
            type="button"
            aria-label="Close"
            @click="dismiss"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <div class="survey-invite-icon" aria-hidden="true">
            <i class="fa-regular fa-star"></i>
          </div>
          <p class="survey-invite-eyebrow">EuRoC apps survey</p>
          <h2 id="survey-invite-title">Help us improve your experience</h2>
          <p id="survey-invite-description" class="survey-invite-copy">
            Tell us how satisfied you are with the EuRoC web and mobile apps.
            The survey is short and your answers are anonymous.
          </p>

          <div class="survey-invite-points">
            <span><i class="fa-solid fa-clock"></i> A few minutes</span>
            <span><i class="fa-solid fa-user-shield"></i> Anonymous</span>
          </div>

          <footer class="survey-invite-actions">
            <button
              class="btn modal-back-action invite-action"
              type="button"
              @click="dismiss"
            >
              <i class="fa-solid fa-xmark"></i>
              Not now
            </button>
            <button
              class="btn survey-primary-action invite-action"
              type="button"
              @click="openSurvey"
            >
              <i class="fa-regular fa-star"></i>
              Evaluate now
            </button>
          </footer>
        </section>
      </div>
    </transition>
  </teleport>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import {
  GET_EDITION_GETTER,
  GET_USER_LEVEL_GETTER,
  GET_USER_TOKEN_GETTER,
  IS_USER_AUTHENTICATED_GETTER,
} from "../../store/storeconstants";
import { isViewerLevel } from "../../modules/accessLevels";

const API_BASE = "https://eurocscoringapi.duartecota.pt/app-evaluations";

export default {
  name: "AppEvaluationInviteModal",
  data() {
    return { dismissed: false, hasSubmitted: false, statusChecked: false };
  },
  computed: {
    ...mapGetters("auth", {
      isAuthenticated: IS_USER_AUTHENTICATED_GETTER,
      levelID: GET_USER_LEVEL_GETTER,
      editionyear: GET_EDITION_GETTER,
      token: GET_USER_TOKEN_GETTER,
    }),
    excludedRoute() {
      return [
        "/login",
        "/register",
        "/resetpassword",
        "/app-evaluation",
        "/mobile-frr",
        "/launch_field",
        "/launch_field_wall",
        "/launch_field_overlay",
      ].includes(String(this.$route.path || "").toLowerCase());
    },
    visible() {
      return (
        this.isAuthenticated &&
        !isViewerLevel(this.levelID) &&
        !this.excludedRoute &&
        this.statusChecked &&
        !this.hasSubmitted &&
        !this.dismissed
      );
    },
  },
  watch: {
    isAuthenticated: {
      immediate: true,
      async handler(value) {
        if (!value) {
          this.dismissed = false;
          this.hasSubmitted = false;
          this.statusChecked = false;
          return;
        }
        this.dismissed = false;
        await this.checkSubmissionStatus();
      },
    },
    editionyear() {
      if (this.isAuthenticated) {
        this.dismissed = false;
        this.checkSubmissionStatus();
      }
    },
  },
  methods: {
    async checkSubmissionStatus() {
      if (!this.editionyear || !this.token) {
        this.statusChecked = true;
        return;
      }
      this.statusChecked = false;
      try {
        const { data } = await axios.get(
          `${API_BASE}/status/${this.editionyear}`,
          { headers: { Authorization: this.token } },
        );
        this.hasSubmitted = Boolean(data?.body?.submitted);
      } catch (_error) {
        this.hasSubmitted = false;
      } finally {
        this.statusChecked = true;
      }
    },
    rememberDismissal() {
      this.dismissed = true;
    },
    dismiss() {
      this.rememberDismissal();
    },
    async openSurvey() {
      this.rememberDismissal();
      if (this.$route.path !== "/app-evaluation") {
        await this.$router.push("/app-evaluation");
      }
    },
  },
};
</script>

<style scoped>
.survey-invite-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1095;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(14, 0, 32, 0.48);
  backdrop-filter: blur(3px);
}

.survey-invite-modal {
  position: relative;
  width: min(92vw, 520px);
  padding: 1.7rem;
  overflow: hidden;
  color: var(--app-text, #172033);
  text-align: center;
  background: var(--app-surface, #fff);
  border: 1px solid var(--app-border, #d8e0ee);
  border-top: 5px solid #3152f5;
  border-radius: 8px;
  box-shadow: 0 26px 70px rgba(14, 0, 32, 0.28);
}

.survey-invite-close {
  position: absolute;
  top: .75rem;
  right: .75rem;
}

.survey-invite-icon {
  display: grid;
  place-items: center;
  width: 66px;
  height: 66px;
  margin: 0 auto 1rem;
  color: #3152f5;
  background: rgba(49, 82, 245, .1);
  border-radius: 50%;
  font-size: 1.65rem;
}

.survey-invite-eyebrow {
  margin: 0 0 .35rem;
  color: #3152f5;
  font-size: .76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.survey-invite-modal h2 {
  margin: 0;
  font-size: 1.55rem;
  font-weight: 900;
}

.survey-invite-copy {
  max-width: 420px;
  margin: .75rem auto 1rem;
  color: var(--app-muted, #667085);
  line-height: 1.55;
}

.survey-invite-points {
  display: flex;
  justify-content: center;
  gap: .65rem;
  flex-wrap: wrap;
  margin-bottom: 1.35rem;
}

.survey-invite-points span {
  padding: .45rem .7rem;
  color: #3152f5;
  background: rgba(49, 82, 245, .08);
  border-radius: 999px;
  font-size: .85rem;
  font-weight: 750;
}

.survey-invite-points i { margin-right: .35rem; }

.survey-invite-actions {
  display: flex;
  justify-content: center;
  gap: .7rem;
  padding-top: 1rem;
  border-top: 1px solid var(--app-border, #e1e7f0);
}

.invite-action {
  width: 160px !important;
  min-height: 44px;
  border: 0 !important;
  font-weight: 800;
}

.survey-primary-action {
  color: #fff !important;
  background: #3152f5 !important;
  box-shadow: 0 10px 20px rgba(49, 82, 245, .2);
}

.survey-primary-action:hover,
.survey-primary-action:focus {
  color: #fff !important;
  background: #2444dc !important;
}

.survey-invite-fade-enter-active,
.survey-invite-fade-leave-active { transition: opacity .2s ease; }
.survey-invite-fade-enter-from,
.survey-invite-fade-leave-to { opacity: 0; }

@media (max-width: 520px) {
  .survey-invite-modal { padding: 1.35rem 1rem 1rem; }
  .survey-invite-actions { flex-direction: column-reverse; }
  .invite-action { width: 100% !important; }
}
</style>
